import { SearchFormTranslations } from '@awazevr/design-system.compositions.search-form'
import { useContext } from 'react'

import { useIntl } from 'react-intl'
import { GlobalProps, useFormatCurrency } from 'src/utils/globalProps'

/**
 * Fetches all the contentful translations, falls back to localization/translation-files
 * The MessageProvider around the app will merge the contentful and static translations together.
 */
export const useSearchformTranslations = (
  driveTimeSearchEnabled = false,
): SearchFormTranslations => {
  const { formatMessage } = useIntl()
  const { formatCurrency } = useFormatCurrency()
  const { brand } = useContext(GlobalProps)
  return {
    destination: {
      searchFormTitle: formatMessage({
        id: 'search.destination.searchFormTitle',
        defaultMessage: 'Your search',
      }),
      inputTitle: formatMessage({
        id: 'search.destination.inputTitle',
        defaultMessage: 'Search destination',
      }),
      accordionTitle: formatMessage({
        id: 'search.destination.accordionTitle',
        defaultMessage: 'Select destination',
      }),
      placeholder: formatMessage({
        id: 'search.destination.placeholder',
        defaultMessage: 'Enter destination',
      }),
      popularAreas: formatMessage({
        id: 'search.destination.popularAreas',
        defaultMessage: 'Popular Areas',
      }),
      popularAreasExpand: formatMessage({
        id: 'search.destination.popularAreasExpand',
        defaultMessage: 'All Popular Areas',
      }),
      popularDestinations: formatMessage({
        id: 'search.destination.popularDestinations',
        defaultMessage: 'Popular Destinations',
      }),
      closeBtn: formatMessage({
        id: 'search.destination.closeBtn',
        defaultMessage: 'Close',
      }),
      clearBtn: formatMessage({
        id: 'search.destination.clearBtn',
        defaultMessage: 'Clear',
      }),
      drawerPlaceholder: formatMessage({
        id: 'search.destination.drawerPlaceholder',
        defaultMessage: 'Browse by country or region',
      }),
      drawerLabel: formatMessage({
        id: 'search.destination.drawerLabel',
        defaultMessage: 'Destination',
      }),
      autocomplete: {
        firstGroupHeading: formatMessage({
          id: 'search.destination.autocomplete.firstGroupHeading',
          defaultMessage: 'Destinations',
        }),
        secondGroupHeading: formatMessage({
          id: 'search.destination.autocomplete.secondGroupHeading',
          defaultMessage: 'Holiday Homes',
        }),
        placeholder: formatMessage({
          id: 'search.destination.autocomplete.placeholder',
          defaultMessage: 'eg. Cumbria or Dorset',
        }),
        error: formatMessage({
          id: 'search.destination.autocomplete.error',
          defaultMessage: 'There was an error.',
        }),
        loading: formatMessage({
          id: 'search.destination.autocomplete.loading',
          defaultMessage: 'Loading...',
        }),
        noResult: formatMessage({
          id: 'search.destination.autocomplete.noResult',
          defaultMessage:
            'We don’t have %s in our destinations list. Try a different search or go back to our destination list.',
        }),
        noResultBtn: formatMessage({
          id: 'search.destination.autocomplete.noResultBtn',
          defaultMessage: 'Browse destinations',
        }),
        clearButtonAriaLabel: formatMessage({
          id: 'search.destination.autocomplete.clearButtonAriaLabel',
          defaultMessage: 'Clear the search field',
        }),
      },
      backBtn: formatMessage({
        id: 'search.destination.backBtn',
        defaultMessage: 'Back',
      }),
      allOfPrefix: formatMessage({
        id: 'search.destination.allOfPrefix',
        defaultMessage: 'All of',
      }),
      rhsHeaders: {
        rhs1: formatMessage({
          id: 'search.destination.rhsHeaders.rhs1',
          defaultMessage: 'Choose country',
        }),
        rhs2: formatMessage({
          id: 'search.destination.rhsHeaders.rhs2',
          defaultMessage: 'Choose region',
        }),
        rhs3: formatMessage({
          id: 'search.destination.rhsHeaders.rhs3',
          defaultMessage: 'Choose area',
        }),
      },
      inputFacadeClearButtonAriaLabel: formatMessage({
        id: 'search.destination.inputFacadeClearButtonAriaLabel',
        defaultMessage: 'clear',
      }),
      iconLabel: formatMessage({
        id: 'search.destination.iconLabel',
        defaultMessage: 'Destination picker',
      }),
      ...(driveTimeSearchEnabled
        ? {
            nearby: {
              ctaTitle: formatMessage({
                id: 'nearby.ctaTitle',
                defaultMessage: 'Search nearby',
              }),
              ctaDescription: formatMessage({
                id: 'nearby.ctaDescription',
                defaultMessage: 'Discover properties around you',
              }),
              destinationField: formatMessage({
                id: 'nearby.destinationField',
                defaultMessage: 'Search nearby',
              }),
              geolocationPermissionWarning: formatMessage({
                id: 'nearby.geolocationPermissionWarning',
                defaultMessage:
                  'You must allow your browser location request to view nearby properties.',
              }),
            },
          }
        : {}),
    },
    searchBar: {
      searchFormSubmitBtn: formatMessage({
        id: 'search.searchBar.searchFormSubmitBtn',
        defaultMessage: 'Search',
      }),
    },
    datesSelector: {
      placeholder: formatMessage({
        id: 'search.datesSelector.placeholder',
        defaultMessage: 'Enter dates',
      }),
      confirm: formatMessage({
        id: 'search.datesSelector.confirm',
        defaultMessage: 'Apply',
      }),
      startDateLabel: formatMessage({
        id: 'search.datesSelector.startDateLabel',
        defaultMessage: 'Start date',
      }),
      startDateInfoText: formatMessage({
        id: 'search.datesSelector.startDateInfoText',
        defaultMessage: 'Get exact prices and availability',
      }),
      startDatePlaceholder: formatMessage({
        id: 'search.datesSelector.startDatePlaceholder',
        defaultMessage: 'Select',
      }),
      startDateAriaLabel: formatMessage({
        id: 'search.datesSelector.startDateAriaLabel',
        defaultMessage: 'Date picker input',
      }),
      durationLabel: formatMessage({
        id: 'search.datesSelector.durationLabel',
        defaultMessage: 'Duration',
      }),
      durationValidationMsg: formatMessage({
        id: 'search.datesSelector.durationValidationMsg',
        defaultMessage:
          'Please select how much flexibilty in dates you wish to have',
      }),
      rangeLabel: formatMessage({
        id: 'search.datesSelector.rangeLabel',
        defaultMessage: 'Flexibility',
      }),
      rangeValidationMsg: formatMessage({
        id: 'search.datesSelector.rangeValidationMsg',
        defaultMessage: 'Please choose the duration of your stay',
      }),
      calendarFormTitle: formatMessage({
        id: 'search.datesSelector.calendarFormTitle',
        defaultMessage: 'Check In and Duration',
      }),
      clearBtn: formatMessage({
        id: 'search.datesSelector.clearBtn',
        defaultMessage: 'Clear',
      }),
      nightLabel: formatMessage({
        id: 'search.datesSelector.nightLabel',
        defaultMessage: 'night',
      }),
      nightsLabel: formatMessage({
        id: 'search.datesSelector.nightsLabel',
        defaultMessage: 'nights',
      }),
      calendarTranslations: {
        nextButtonTitle: formatMessage({
          id: 'search.datesSelector.calendarTranslations.nextButtonTitle',
          defaultMessage: 'View next month',
        }),
        prevButtonTitle: formatMessage({
          id: 'search.datesSelector.calendarTranslations.prevButtonTitle',
          defaultMessage: 'View previous month',
        }),
        unavailabilityHeadingText: formatMessage({
          id: 'search.datesSelector.calendarTranslations.unavailabilityHeadingText',
          defaultMessage: "There aren't any available dates",
        }),
        unavailabilityMessageText: formatMessage({
          id: 'search.datesSelector.calendarTranslations.unavailabilityMessageText',
          defaultMessage:
            "We couldn't find any available dates for your trip duration. Please change the duration and try again.",
        }),
        formatPrice: formatCurrency,
      },
      inputFacadeClearButtonAriaLabel: formatMessage({
        id: 'search.datesSelector.inputFacadeClearButtonAriaLabel',
        defaultMessage: 'clear',
      }),
      closeBtn: formatMessage({
        id: 'search.datesSelector.closeBtn',
        defaultMessage: 'Close',
      }),
      decline: formatMessage({
        id: 'search.datesSelector.decline',
        defaultMessage: 'decline',
      }),
    },
    partySelector: {
      infants: {
        validationMsg: formatMessage({
          id: 'search.partySelector.infants.validationMsg',
          defaultMessage: 'Please choose how many infants are going',
        }),
        label: formatMessage({
          id: 'search.partySelector.infants.label',
          defaultMessage: 'Infants',
        }),
        description: formatMessage({
          id: 'search.partySelector.infants.description',
          defaultMessage: 'Aged up to 2',
        }),
      },
      children: {
        validationMsg: formatMessage({
          id: 'search.partySelector.children.validationMsg',
          defaultMessage: 'Please choose how many children are going',
        }),
        label: formatMessage({
          id: 'search.partySelector.children.label',
          defaultMessage: 'Children',
        }),
        description: formatMessage({
          id:
            brand === 'cottages.com' || brand === 'hoseasons'
              ? 'search.partySelector.children.description-uk'
              : 'search.partySelector.children.description',
          defaultMessage: 'Aged 0-11',
        }),
      },
      adults: {
        validationMsg: formatMessage({
          id: 'search.partySelector.adults.validationMsg',
          defaultMessage: 'Please choose how many adults are going',
        }),
        label: formatMessage({
          id: 'search.partySelector.adults.label',
          defaultMessage: 'Adults',
        }),
      },
      pets: {
        validationMsg: formatMessage({
          id: 'search.partySelector.pets.validationMsg',
          defaultMessage: 'Please choose how many pets are going',
        }),
        label: formatMessage({
          id: 'search.partySelector.pets.label',
          defaultMessage: 'Pets',
        }),
      },
      summary: {
        guest: formatMessage({
          id: 'search.partySelector.summary.guest',
          defaultMessage: 'guest',
        }),
        guestPlural: formatMessage({
          id: 'search.partySelector.summary.guestPlural',
          defaultMessage: 'guests',
        }),
        pet: formatMessage({
          id: 'search.partySelector.summary.pet',
          defaultMessage: 'pet',
        }),
        petPlural: formatMessage({
          id: 'search.partySelector.summary.petPlural',
          defaultMessage: 'pets',
        }),
      },
      drawer: {
        heading: formatMessage({
          id: 'search.partySelector.drawer.heading',
          defaultMessage: 'Guests',
        }),
        closeLabel: formatMessage({
          id: 'search.partySelector.drawer.closeLabel',
          defaultMessage: 'Close',
        }),
        buttonLabel: formatMessage({
          id: 'search.partySelector.drawer.buttonLabel',
          defaultMessage: 'Apply',
        }),
      },
      partySizeInput: {
        iconLabel: formatMessage({
          id: 'search.partySelector.partySizeInput.iconLabel',
          defaultMessage: 'Party size',
        }),
        placeholder: formatMessage({
          id: 'search.partySelector.partySizeInput.placeholder',
          defaultMessage: 'Enter Party',
        }),
      },
      inputFacadeClearButtonAriaLabel: formatMessage({
        id: 'search.partySelector.inputFacadeClearButtonAriaLabel',
        defaultMessage: 'clear fields',
      }),
    },
    summaryStrings: {
      hintLabel: formatMessage({
        id: 'search.summaryStrings.hintLabel',
        defaultMessage: 'Where would you like to go?',
      }),
      defaultPlaceholder: formatMessage({
        id: 'search.summaryStrings.defaultPlaceholder',
        defaultMessage: 'Add destination, dates, guests',
      }),
      dateNoLocationPlaceholder: formatMessage({
        id: 'search.summaryStrings.dateNoLocationPlaceholder',
        defaultMessage: 'Add destination',
      }),
      locationNoDatePlaceholder: formatMessage({
        id: 'search.summaryStrings.locationNoDatePlaceholder',
        defaultMessage: 'Add date for exact prices and availability',
      }),
      daysLabel: formatMessage({
        id: 'search.summaryStrings.daysLabel',
        defaultMessage: 'days',
      }),
      nightLabel: formatMessage({
        id: 'search.summaryStrings.nightLabel',
        defaultMessage: 'night',
      }),
      nightsLabel: formatMessage({
        id: 'search.summaryStrings.nightsLabel',
        defaultMessage: 'nights',
      }),
      guestLabel: formatMessage({
        id: 'search.summaryStrings.guestLabel',
        defaultMessage: 'guest',
      }),
      guestsLabel: formatMessage({
        id: 'search.summaryStrings.guestsLabel',
        defaultMessage: 'guests',
      }),
      searchIconTitle: formatMessage({
        id: 'search.summaryStrings.searchIconTitle',
        defaultMessage: 'Open search panel',
      }),
    },
    accommodationSelector: {
      placeholder: formatMessage({
        id: 'search.accommodationSelector.placeholder',
        defaultMessage: 'Accommodation type',
      }),
      inputFacadeClearButtonAriaLabel: formatMessage({
        id: 'search.accommodationSelector.inputFacadeClearButtonAriaLabel',
        defaultMessage: 'clear fields',
      }),
    },
  }
}
